// react_frontend/src/Components/Modules/UserSetting/EmployeeList.js
import React, { useEffect, useState, useMemo, useContext } from "react";
import { MotherContext } from "../../../Context/MotherContext";
import Breadcrumb from "../../Partials/Breadcrumb";
import DataTable from "react-data-table-component";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import moment from "moment";
import {
  Button,
  Input,
  Badge,
  Loader,
  Whisper,
  Tooltip,
  Tag,
} from "rsuite";
import { FaUserTie, FaEnvelope, FaPhone, FaBriefcase, FaDollarSign, FaMapMarkerAlt, FaEdit, FaToggleOn, FaToggleOff, FaTrashAlt, FaSearch } from "react-icons/fa";
import "rsuite/dist/rsuite.min.css";

// Status Badge
const StatusBadge = ({ active }) => (
  <Badge
    color={active ? "green" : "red"}
    content={active ? "Active" : "Inactive"}
    style={{ fontSize: "0.8em", padding: "4px 8px" }}
  />
);

// Action Buttons with Tooltips
const ActionButtons = ({ employee, onDelete, onStatusToggle }) => {
  const currentUserId = localStorage.getItem("id");
  const currentUserRole = parseInt(localStorage.getItem("who_am_i"));
  const isMasterOrAdmin = currentUserRole === 1 || currentUserRole === 2;
  const isMasterOnly = currentUserRole === 1;

  return (
    <div className="d-flex gap-1 justify-content-center flex-wrap">
      {isMasterOrAdmin && (
        <>
          {/* Edit Button */}
          <Whisper placement="top" speaker={<Tooltip>Edit employee details</Tooltip>}>
            <Link to={`/edit_employee/${employee.id}`} style={{ textDecoration: "none" }}>
              <Button size="sm" color="blue" startIcon={<FaEdit />}>
                Edit
              </Button>
            </Link>
          </Whisper>

          {/* Status Toggle */}
          <Whisper
            placement="top"
            speaker={
              <Tooltip>
                {employee.employee_status ? "Deactivate employee" : "Activate employee"}
              </Tooltip>
            }
          >
            <Button
              size="sm"
              color={employee.employee_status ? "green" : "red"}
              startIcon={employee.employee_status ? <FaToggleOn /> : <FaToggleOff />}
              onClick={() => onStatusToggle(employee)}
            >
              {employee.employee_status ? "Active" : "Inactive"}
            </Button>
          </Whisper>
        </>
      )}

      {isMasterOnly && (
        // <Whisper placement="top" speaker={<Tooltip>Delete employee permanently</Tooltip>}  preventOverflow={false}>
        <Button
          size="sm"
          color="red"
          startIcon={<FaTrashAlt />}
          onClick={() => onDelete(employee)}
        >
          Delete
        </Button>
        // </Whisper>
      )}
    </div>
  );
};

const EmployeeList = () => {
  const { employees, removeEmployeeFromList, updateEmployeeInList, loadingEmployees, startEmployeePolling, stopEmployeePolling, } = useContext(MotherContext);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  // Fetch employees
  useEffect(() => {
    startEmployeePolling();
    return () => stopEmployeePolling();
  }, [startEmployeePolling, stopEmployeePolling]);

  // Handle Delete
  const handleDelete = (employee) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to recover this employee!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel",
      reverseButtons: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await AxiosAuthInstance.delete(`/add_employee/${employee.id}`);
          removeEmployeeFromList(employee.id);
          Swal.fire("Deleted!", "Employee has been deleted.", "success");
        } catch (err) {
          Swal.fire("Error", "Failed to delete employee.", "error");
        }
      }
    });
  };

  // Handle Status Toggle
  const handleStatusToggle = (employee) => {
    const newStatus = employee.employee_status === 1 ? 0 : 1;

    Swal.fire({
      title: `Are you sure?`,
      text: `You will mark this employee as ${newStatus ? "Active" : "Inactive"}.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Yes, ${newStatus ? "Activate" : "Deactivate"}`,
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await AxiosAuthInstance.post(
            `/update_employee_status/${employee.id}/${newStatus}`
          );
          updateEmployeeInList({ ...employee, employee_status: newStatus });
          Swal.fire("Updated!", "Employee status has been changed.", "success");
        } catch (err) {
          Swal.fire("Error", "Failed to update status.", "error");
        }
      }
    });
  };

  // Filtered employees Data
  const filteredData = useMemo(() => {
    return employees.filter((emp) =>
      emp?.employee_name.toLowerCase().includes(search.toLowerCase()) ||
      emp?.employee_designation?.toLowerCase().includes(search.toLowerCase()) ||
      emp?.employee_phone?.toLowerCase().includes(search.toLowerCase()) ||
      emp?.employee_email?.toLowerCase().includes(search.toLowerCase())
    );
  }, [employees, search]);

  // Columns
  const columns = [
    {
      name: "SL",
      cell: (row, index) => <strong>{index + 1}</strong>,
      width: "60px",
      center: true,
    },
    {
      name: "Name",
      selector: (row) => row.employee_name,
      sortable: true,
      grow: 2,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FaUserTie className="text-primary" />
          <span className="fw-medium">{row.employee_name}</span>
        </div>
      ),
    },
    {
      name: "Email",
      selector: (row) => row.employee_email,
      sortable: true,
      grow: 2,
      cell: (row) => (
        <div className="d-flex align-items-center gap-1">
          <FaEnvelope className="text-secondary" />
          <span>{row.employee_email}</span>
        </div>
      ),
    },
    {
      name: "Phone",
      selector: (row) => row.employee_phone,
      sortable: true,
      width: "130px",
      cell: (row) => (
        <div className="d-flex align-items-center gap-1">
          <FaPhone className="text-secondary" />
          <span>{row.employee_phone}</span>
        </div>
      ),
    },
    {
      name: "ID No",
      selector: (row) => row.nid,
      sortable: true,
      width: "130px",
      cell: (row) => (
        <div className="d-flex align-items-center gap-1">
          <span>{row.nid}</span>
        </div>
      ),
    },
    {
      name: "Designation",
      selector: (row) => row.employee_designation,
      sortable: true,
      width: "150px",
      cell: (row) => (
        <Tag color="blue" style={{ fontSize: "0.9em" }}>
          <FaBriefcase /> {row.employee_designation}
        </Tag>
      ),
    },
    {
      name: "Joining",
      selector: (row) => row.joining_date,
      sortable: true,
      width: "140px",
      cell: (row) => (
        <small>
          {moment(row.joining_date).format("MMM DD, YYYY")}
        </small>
      ),
    },
    {
      name: "Salary",
      selector: (row) => row.gross_salary,
      sortable: true,
      width: "120px",
      cell: (row) => (
        <div className="d-flex align-items-center gap-1">
          <FaDollarSign className="text-success" />
          <strong>{Number(row.gross_salary).toLocaleString("en-BD")}</strong>
        </div>
      ),
    },
    {
      name: "Address",
      selector: (row) => row.address,
      sortable: true,
      width: "180px",
      wrap: true,
      cell: (row) => (
        <div className="d-flex align-items-start gap-1 small">
          <FaMapMarkerAlt className="text-secondary mt-1" />
          <span>{row.address || "—"}</span>
        </div>
      ),
    },
    {
      name: "Status",
      width: "110px",
      center: true,
      cell: (row) => <StatusBadge active={row.employee_status} />,
    },
    {
      name: "Actions",
      cell: (row) => (
        <ActionButtons
          employee={row}
          onDelete={handleDelete}
          onStatusToggle={handleStatusToggle}
        />
      ),
      width: "260px",
      center: true,
    },
  ];

  // Custom Styling
  const customStyles = {
    head: {
      style: {
        fontSize: "14px",
        fontWeight: "bold",
        backgroundColor: "#f8f9fa",
      },
    },
    rows: {
      style: {
        fontSize: "14px",
        minHeight: "60px",
      },
    },
    cells: {
      style: {
        padding: "12px 8px",
      },
    },
  };

  const AddEmployeeButton = () => (
    <Whisper placement="top" speaker={<Tooltip>Add a new employee</Tooltip>}>
      <Link to="/add_employee" style={{ textDecoration: "none" }}>
        <Button
          appearance="primary"
          startIcon={<FaUserTie />}
          className="d-flex align-items-center custom-bg"
        >
          Add Employee
        </Button>
      </Link>
    </Whisper>
  );

  return (
    <>
      <Breadcrumb title="Employee Management" />

      <div className="p-3 bg-white rounded shadow-sm mb-4 d-flex flex-wrap gap-3 align-items-center justify-content-between">
        <h5 className="text-dark mb-0">
          <i className="fas fa-users-cog me-2"></i>
          Employees
        </h5>

        <div className="d-flex gap-2 w-100 w-md-auto">
          <Input
            placeholder="Search by name, email, phone, designation..."
            value={search}
            onChange={setSearch}
            prefix={<FaSearch />}
            style={{ width: 320 }}
            size="md"
          />
          {(localStorage.getItem("who_am_i") === "1" ||
            localStorage.getItem("who_am_i") === "2") && <AddEmployeeButton />}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredData}
        pagination
        paginationPerPage={50}
        paginationRowsPerPageOptions={[50, 100, 150, 200]}
        progressPending={loadingEmployees}
        progressComponent={
          <div className="d-flex justify-content-center my-5">
            <Loader size="md" /> <span className="ms-2">Loading employees...</span>
          </div>
        }
        noDataComponent={
          <div className="text-center my-5 text-muted">
            <h5>No employees found</h5>
            <p>Try adjusting your search or add a new employee.</p>
          </div>
        }
        highlightOnHover
        responsive
        fixedHeader
        fixedHeaderScrollHeight="600px"
        customStyles={customStyles}
        striped
      />
    </>
  );
};

export default EmployeeList;